import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Smartphone, ShieldCheck, FlaskConical, PlugZap, Save, Loader2 } from "lucide-react";
import { API_URL, formatCurrency, formatDate } from "../../lib/utils";

const EMPTY = { env: "sandbox", client_id: "", client_secret: "", gpo_key: "", ref_key: "", enabled: true };

export const AppyPaySettings = ({ headers, basePath = "/payments/appypay/config", title = "AppyPay — Multicaixa Express & Referências", description, showTest = true, dark = false, webhookTarget }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}${basePath}`, { headers, withCredentials: true });
      setCfg(r.data);
      setForm({ env: r.data.env, client_id: r.data.client_id, client_secret: "", gpo_key: r.data.gpo_key, ref_key: r.data.ref_key, enabled: r.data.enabled });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar configuração AppyPay");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, client_secret: form.client_secret.trim() ? form.client_secret.trim() : null };
      const r = await axios.put(`${API_URL}${basePath}`, body, { headers, withCredentials: true });
      setCfg({ ...cfg, ...r.data });
      setForm((f) => ({ ...f, client_secret: "" }));
      toast.success(r.data.configured ? "AppyPay configurada" : "Guardado — AppyPay em modo SIMULADO até preencher as credenciais");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar", { duration: 7000 });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API_URL}${basePath}/test`, {}, { headers, withCredentials: true });
      setTestResult({ ok: true, message: `${r.data.message} (${r.data.env})` });
      toast.success(`${r.data.message} (${r.data.env})`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Falha na ligação à AppyPay";
      setTestResult({ ok: false, message: msg });
      toast.error("Ligação à AppyPay recusada — veja o detalhe abaixo do botão", { duration: 6000 });
    } finally {
      setTesting(false);
    }
  };

  const webhookUrl = `${API_URL}/payments/appypay/webhook/${webhookTarget || "{company_id}"}`;
  const muted = dark ? "text-white/50" : "text-muted-foreground";

  return (
    <Card className={dark ? "bg-white/5 border-white/10 text-white" : ""} data-testid="appypay-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Smartphone className="w-5 h-5 text-emerald-500" /> {title}
          {cfg && (cfg.configured
            ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid="appypay-status-badge">Configurada · {cfg.env === "production" ? "Produção" : "Sandbox"}</Badge>
            : <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30" data-testid="appypay-status-badge"><FlaskConical className="w-3 h-3 mr-1" /> MODO SIMULADO</Badge>)}
        </CardTitle>
        <CardDescription className={dark ? "text-white/50" : ""}>
          {description || "Credenciais obtidas no portal de programadores AppyPay (app.appypay.co.ao ou app-tst para testes). O Client Secret é guardado encriptado e nunca é mostrado."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cfg && !cfg.configured && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm" data-testid="appypay-simulated-notice">
            <strong>Sem credenciais:</strong> os pagamentos Multicaixa Express e as referências funcionam em <strong>modo SIMULADO</strong> (confirmação automática ao fim de alguns segundos, sem movimentar dinheiro real). Preencha os campos abaixo para ativar pagamentos reais.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ambiente</Label>
            <Select value={form.env} onValueChange={(v) => setForm({ ...form, env: v })}>
              <SelectTrigger data-testid="appypay-env-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox (testes — app-tst.appypay.co.ao)</SelectItem>
                <SelectItem value="production">Produção (app.appypay.co.ao)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-lg border px-3 h-10 mt-auto">
            <Label className="cursor-pointer">Pagamentos AppyPay ativos</Label>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="appypay-enabled-switch" />
          </div>
          <div className="space-y-2">
            <Label>Client ID</Label>
            <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono" data-testid="appypay-client-id" />
          </div>
          <div className="space-y-2">
            <Label>Client Secret {cfg?.client_secret_set && <span className={`text-xs ${muted}`}>(definido — deixe vazio para manter)</span>}</Label>
            <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder={cfg?.client_secret_set ? "••••••••••••" : "Client Secret"} className="font-mono" data-testid="appypay-client-secret" />
          </div>
          <div className="space-y-2">
            <Label>Chave Multicaixa Express (GPO_…)</Label>
            <Input value={form.gpo_key} onChange={(e) => setForm({ ...form, gpo_key: e.target.value })} placeholder="GPO_xxxxxxxx" className="font-mono" data-testid="appypay-gpo-key" />
          </div>
          <div className="space-y-2">
            <Label>Chave Referências (REF_…)</Label>
            <Input value={form.ref_key} onChange={(e) => setForm({ ...form, ref_key: e.target.value })} placeholder="REF_xxxxxxxx" className="font-mono" data-testid="appypay-ref-key" />
          </div>
        </div>
        <div className={`rounded-lg border p-3 text-xs space-y-1 ${muted}`}>
          <p className="flex items-center gap-1.5 font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> Webhook (configurar no portal AppyPay, método POST)</p>
          <code className="block break-all font-mono text-[11px]" data-testid="appypay-webhook-url">{webhookUrl}</code>
          <p>Mesmo sem webhook, o sistema confirma os pagamentos por consulta automática ao estado da cobrança.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="appypay-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </Button>
          {showTest && (
            <Button variant="outline" onClick={test} disabled={testing || !cfg?.configured} className="gap-2" data-testid="appypay-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Testar ligação
            </Button>
          )}
          {cfg?.updated_at && <span className={`text-xs self-center ${muted}`}>Atualizado {formatDate(cfg.updated_at)} por {cfg.updated_by_name}</span>}
        </div>
        {testResult && (
          <div className={`rounded-lg border p-3 text-sm ${testResult.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700" : "border-red-500/30 bg-red-500/5 text-red-700"}`} data-testid="appypay-test-result">
            <p className="font-semibold">{testResult.ok ? "Ligação confirmada" : "Ligação recusada pela AppyPay"}</p>
            <p className="break-words">{testResult.message}</p>
            {!testResult.ok && (
              <ul className="list-disc ml-5 mt-2 text-xs space-y-0.5 opacity-90">
                <li>Client ID = identificador da aplicação (GUID). As chaves GPO_/REF_ vão nos campos próprios.</li>
                <li>Client Secret = o <strong>valor</strong> do segredo (não o ID do segredo), sem espaços.</li>
                <li>Credenciais criadas em app-tst.appypay.co.ao só funcionam em <strong>Sandbox</strong>; as de app.appypay.co.ao só em <strong>Produção</strong>.</li>
              </ul>
            )}
          </div>
        )}
        <p className={`text-xs ${muted}`}>
          Como obter: portal AppyPay → Programadores / API → criar aplicação → copiar <em>Client ID</em> e gerar <em>Client Secret</em>; nos métodos de pagamento copiar o identificador <em>GPO_…</em> (Multicaixa Express) e/ou <em>REF_…</em> (Referências).
        </p>
        {cfg?.payments && (
          <div className="pt-2">
            <p className="text-sm font-semibold mb-2">Pagamentos de planos recebidos ({cfg.payments.length})</p>
            <div className="max-h-56 overflow-y-auto divide-y rounded-lg border text-sm" data-testid="plan-payments-list">
              {cfg.payments.length === 0 && <p className={`p-3 ${muted}`}>Ainda sem pagamentos</p>}
              {cfg.payments.map((p) => (
                <div key={p.payment_id} className="flex items-center justify-between gap-3 p-2.5">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.company_name} · {p.plan === "ouro" ? "Ouro" : "Bronze"} ({p.billing_cycle === "annual" ? "anual" : "mensal"}){p.simulated && <span className="ml-1 text-[10px] text-amber-500 font-bold">SIMULADO</span>}</p>
                    <p className={`text-xs ${muted}`}>{formatDate(p.paid_at)} · ativo até {new Date(p.subscription_ends_at).toLocaleDateString("pt-PT")}</p>
                  </div>
                  <span className="font-mono font-semibold shrink-0">{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
